import React from 'react';
import {Column} from 'react-table';
import {makeStyles} from '@material-ui/core/styles';
import {TFunction} from 'react-i18next';
import {Colors, white} from '../../../styles/Colors';
import {AccountListItem} from '../../../store/actions/accounts/payloads';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import {Edit} from '@material-ui/icons';
import {ReactComponent as DataUsageIcon} from '../../../assets/data_usage.svg';
import dayjs from '../../../services/customDayJs';
import {convertFormatFromBackendToDayJs} from '../../../utils/dateWithTimezoneConversion';
import {getRoute, Routes} from '../../../routes/routes';
import PermissionLinkCell from '../../../components/DataTable/PermissionLinkCell';
import OverflowTooltip from '../../../components/OverflowTooltip/OverflowTooltip';
import history from '../../../history';
import {Permission} from '../../../store/types/Permission';
import EmptyRowContent from "../../../components/DataTable/EmptyRowContent";
import PermissionProvider from '../../../components/PermissionProvider/PermissionProvider';

export const useStyles = makeStyles(() => ({
    mainContainer: {
        flexDirection: 'column',
        height: '100vh',
        display: 'flex',
        '& .MuiButton-label': {
            color: white,
        },
        flex: 1,
        position: 'relative',
        background: Colors.SmokeBackground,
    },
    scrollable: {
        overflowY: 'auto',
        flex: 1,
        padding: '48px 96px',
        '& .MuiTableContainer-root': {
            width: 'unset !important',
            overflowX: 'hidden',
        },
    },
    content: {
        maxWidth: 1040,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    filtersContainer: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: 8,
        gap: 16,
    },
    filtersInputs: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        flex: 1,
    },
    filterField: {
        width: 180,
    },
    statusCell: {
        display: 'flex',
        flexDirection: 'column',
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
    },
    statusDot: {
        display: 'inline-flex',
        marginRight: 9,
        width: 10,
        height: 10,
        borderRadius: 25,
        flexShrink: 0,
    },
    statusDotActive: {
        backgroundColor: Colors.Support,
    },
    statusDotNotYetActive: {
        backgroundColor: Colors.Link,
    },
    statusDotSuspended: {
        backgroundColor: Colors.Primary2,
    },
    statusDotInactive: {
        backgroundColor: Colors.Gray9,
    },
    statusText: {
        fontSize: 16,
        fontWeight: 500,
    },
    statusActive: {
        color: Colors.Support,
    },
    statusNotYetActive: {
        color: Colors.Link,
    },
    statusSuspended: {
        color: Colors.Primary2,
    },
    statusInactive: {
        color: Colors.Gray9,
    },
    lastUsed: {
        fontSize: 12,
        color: Colors.Gray9,
    },
    actionsCell: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        '& .MuiIconButton-root': {
            padding: 6,
        },
    },
    actionHeader: {
        textAlign: 'center',
    },
    cityAddressCell: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    cityText: {
        fontSize: 14,
        fontWeight: 400,
        color: Colors.Text,
        lineHeight: 'normal',
    },
    addressText: {
        fontSize: 14,
        fontWeight: 400,
        color: Colors.Text,
        lineHeight: 'normal',
    },
    planCell: {
        fontWeight: 600,
        color: Colors.Text,
    },
    tableColumns: {
        '& .MuiTableCell-root:first-child': {
            paddingLeft: '40px !important',
        },
        '& .MuiTableCell-root:last-child': {
            paddingRight: '14px !important',
        },
    },
}));

const isFutureDate = (date?: string) => !!date && dayjs(date).isAfter(dayjs());

/**
 * An account is "not yet active" when it is otherwise active ('O')
 * but its activation date has not arrived yet.
 */
export const isNotYetActive = (account?: {
    bill_status?: string;
    activation_date?: string;
}) => account?.bill_status === 'O' && isFutureDate(account.activation_date);

export function formatLastUsage(
    lastUsage?: string,
    activationDate?: string,
    t?: TFunction<string>,
    userDateFormat?: string,
): string {
    const format = userDateFormat
        ? convertFormatFromBackendToDayJs(userDateFormat)
        : 'YYYY-MM-DD';

    if (!lastUsage) {
        if (activationDate) {
            const date = dayjs(activationDate).format(format);
            if (isFutureDate(activationDate)) {
                return t
                    ? t<string>('screens:accounts.activatesFrom', {date})
                    : `From ${date}`;
            }
            return t
                ? t<string>('screens:accounts.lastUsedSince', {date})
                : `Since ${date}`;
        }
        return '—';
    }

    const date = dayjs(lastUsage);
    const now = dayjs();
    const diffDays = now.diff(date, 'day');

    if (diffDays === 0) {
        return t
            ? t<string>('screens:accounts.lastUsedTodayAt', {time: date.format('HH:mm')})
            : `Today at ${date.format('HH:mm')}`;
    } else if (diffDays === 1) {
        return t
            ? t<string>('screens:accounts.lastUsedYesterdayAt', {time: date.format('HH:mm')})
            : `Yesterday at ${date.format('HH:mm')}`;
    } else {
        return date.fromNow();
    }
}

export function getBillStatusLabel(
    billStatus: string | undefined,
    t: TFunction<string>,
    activationDate?: string,
): string {
    switch (billStatus) {
        case 'O': return isNotYetActive({bill_status: billStatus, activation_date: activationDate})
            ? t<string>('screens:accounts.statusNotYetActive')
            : t<string>('screens:accounts.active');
        case 'S': return t<string>('screens:accounts.suspended');
        case 'I': return t<string>('screens:accounts.inactive');
        default:  return billStatus ?? '—';
    }
}

export type BillStatusVariant = 'active' | 'notYetActive' | 'suspended' | 'inactive';

export const getBillStatusVariant = (
    billStatus?: string,
    activationDate?: string,
): BillStatusVariant => {
    if (billStatus === 'O') {
        return isNotYetActive({bill_status: billStatus, activation_date: activationDate})
            ? 'notYetActive'
            : 'active';
    }
    if (billStatus === 'S') return 'suspended';
    return 'inactive';
};

export const generateAccountListColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    onClickEdit: (item: AccountListItem) => void,
    onClickTransactions: (item: AccountListItem) => void,
    userDateFormat?: string,
): Column<AccountListItem>[] => [
    {
        accessor: 'id',
        Header: t<string>('screens:accounts.accountId'),
        width: 1,
        Cell: function Cell({row}) {
            return (
                <PermissionLinkCell
                    text={String(row.original.id)}
                    onClick={() =>
                        history.push(
                            getRoute(Routes.AccountDetails, {id: row.original.id}),
                        )
                    }
                    permissions={Permission.Accounts.AccountDetails.value}
                />
            );
        },
    },
    {
        accessor: 'email',
        Header: t<string>('common:emailAddress'),
        width: 1.2,
        Cell: function Cell({row}) {

            const EMPTY_STRING = '—';
            const email = row.original.email ?? EMPTY_STRING;

            if(email === EMPTY_STRING)
            {
                return <EmptyRowContent/>;

            }

            return <OverflowTooltip text={email} tooltip={email} copy={false}/>;
        },
    },
    {
        accessor: 'product_visible_name',
        Header: t<string>('screens:accounts.plan'),
        width: 1,
        Cell: function Cell({row}) {
            const plan = row.original.product_visible_name ?? '—';
            return <OverflowTooltip text={plan} tooltip={plan} copy={false} classes={{text: classes.planCell}}/>;
        },
    },
    {
        accessor: 'city',
        Header: t<string>('screens:accounts.cityAddress'),
        width: 1.2,
        Cell: function Cell({row}) {
            const city = row.original.city;
            const address = row.original.baddr1;
            if (!city && !address) return <EmptyRowContent/>;

            return (
                <div className={classes.cityAddressCell}>
                    {city && <OverflowTooltip text={city} tooltip={city} copy={false} classes={{text: classes.cityText}}/>}
                    {address && <OverflowTooltip text={address} tooltip={address} copy={false} classes={{text: classes.addressText}}/>}
                </div>
            );
        },
    },
    {
        accessor: 'bill_status',
        Header: t<string>('screens:accounts.statusLastUsed'),
        width: 1.2,
        Cell: function Cell({row}) {
            const status = row.original.bill_status;
            const activationDate = row.original.activation_date;
            const label = getBillStatusLabel(status, t, activationDate);
            const variant = getBillStatusVariant(status, activationDate);
            const hideDateRow = status === 'I' || status === 'S';
            const lastUsed = hideDateRow ? '' : formatLastUsage(row.original.last_usage, activationDate, t, userDateFormat);

            const dotClass = {
                active: classes.statusDotActive,
                notYetActive: classes.statusDotNotYetActive,
                suspended: classes.statusDotSuspended,
                inactive: classes.statusDotInactive,
            }[variant];

            const labelClass = {
                active: classes.statusActive,
                notYetActive: classes.statusNotYetActive,
                suspended: classes.statusSuspended,
                inactive: classes.statusInactive,
            }[variant];

            return (
                <div className={classes.statusCell}>
                    <div className={classes.statusRow}>
                        <span className={`${classes.statusDot} ${dotClass}`} />
                        <span className={`${classes.statusText} ${labelClass}`}>{label}</span>
                    </div>
                    {lastUsed && <span className={classes.lastUsed}>{lastUsed}</span>}
                </div>
            );
        },
    },
    {
        accessor: 'i_account',
        Header: (
            <div className={classes.actionHeader}>
                {t<string>('screens:accounts.actions')}
            </div>
        ),
        width: 0.6,
        Cell: function Cell({row}) {
            return (
                <div className={classes.actionsCell}>
                    <PermissionProvider permission={Permission.Accounts.ViewTransactionHistory.value}>
                        <CustomizedIconButton
                            tooltipText={t('screens:accounts.viewTransactions')}
                            dataQa="transactions-icon"
                            onClick={() => onClickTransactions(row.original)}
                        >
                            <DataUsageIcon/>
                        </CustomizedIconButton>
                    </PermissionProvider>
                    <CustomizedIconButton
                        tooltipText={t('screens:accounts.editAccount')}
                        dataQa="edit-icon"
                        onClick={() => onClickEdit(row.original)}
                        skipPermission
                    >
                        <Edit/>
                    </CustomizedIconButton>
                </div>
            );
        },
    },
];
